import { useEffect, useRef, useState } from 'react';
import {
    Box,
    styled,
    Label,
    Select,
    AppText,
    Option,
    Column,
    Row,
    AppIcon,
    useTheme,
} from '@streem/ui-react';
import { recordElementClicked } from '@streem/analytics';
import {
    getMediaDevices,
    getPreferredVideoInputId,
    setPreferredVideoInputId,
} from '@streem/toolbox';
import appLogger from '../../util/logging/app_logger';
import { getCameraFeed, getInitialValue, mediaStream } from './media_settings_utils';
import { cleanDeviceLabel } from '../../util/clean_device_labels';

const log = appLogger.extend('VideoOutputSelect');

// abbreviated wih ms- prefix in case we add analytics to the in call experience in the future
const videoDropdownOpenedEvent = 'ms-video-output-select-opened';
const videoOptionSelectedEvent = 'ms-video-output-item-selected';

export const VideoOutputSelect = () => {
    const theme = useTheme();
    const [dropdownValue, setDropdownValue] = useState<Option<string> | null>(null);
    const [renderVideo, setRenderVideo] = useState(false);
    const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[] | []>([]);
    const [{ error, errorMessage }, setError] = useState({ error: false, errorMessage: '' });
    const [stream, setStream] = useState<MediaStream | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    const handleSelect = (option: Option<string>) => {
        log.info(
            `setting preferred video device: label=${option.label}, deviceId=${option.value} `,
        );
        getCameraFeed(option.value).then(stream => {
            if (stream) {
                setStream(stream);
            }
        });
        setPreferredVideoInputId(option.value);
        setDropdownValue(option);
        recordElementClicked(videoOptionSelectedEvent);
    };

    const onMenuOpen = () => {
        recordElementClicked(videoDropdownOpenedEvent);
    };

    useEffect(() => {
        const preferredCameraId = getPreferredVideoInputId();

        let devices: MediaDeviceInfo[] = [];
        const setupCameraAndDevices = async () => {
            try {
                await getCameraFeed(preferredCameraId);
                devices = await getMediaDevices('videoinput');
                setVideoDevices(
                    devices.map(device => ({
                        ...device.toJSON(),
                        label: cleanDeviceLabel(device.label),
                    })),
                );
            } catch (error) {
                setError({
                    error: true,
                    errorMessage: 'Streem requires permission to use your camera for video chat',
                });
                log.error(
                    `Unable to get camera permission on setupCameraAndDevices. Browser error: ${error}`,
                );
                return;
            }

            if (mediaStream) {
                const [mediaStreamTrack] = mediaStream.getVideoTracks();
                setDropdownValue(getInitialValue(devices, mediaStreamTrack, preferredCameraId));
                setRenderVideo(true);
                setStream(mediaStream);
            }
        };

        setupCameraAndDevices();

        return () => {
            if (mediaStream) {
                mediaStream.getVideoTracks().forEach(track => track.stop());
            }
        };
    }, []);

    useEffect(() => {
        if (stream && videoRef.current) {
            videoRef.current.srcObject = stream;
        }
    }, [stream]);

    return (
        <Wrapper>
            <VideoContainer>
                {renderVideo && !error && (
                    <Video data-testid="video-element" autoPlay playsInline muted ref={videoRef} />
                )}
                {error && (
                    <PermissionDeniedContainer data-testid="video-permission-denied-messages">
                        <AppText size="large" color="white" style={{ textAlign: 'center' }}>
                            {errorMessage}
                        </AppText>
                        <AppText size="small" color="white">
                            Please grant camera permission in browser settings{' '}
                        </AppText>
                    </PermissionDeniedContainer>
                )}
            </VideoContainer>

            <DropDownWrapper data-clarity-mask="true">
                <DropdownHeader size="medium" semibold data-clarity-unmask="true">
                    Video
                </DropdownHeader>
                <Label size="medium" semibold data-clarity-unmask="true">
                    Select Camera
                </Label>
                <Select
                    onMenuOpen={onMenuOpen}
                    id="video-output-select"
                    border={true}
                    isSearchable={false}
                    placeholder="Select camera"
                    value={dropdownValue}
                    options={videoDevices?.map(device => ({
                        value: device.deviceId,
                        label: device.label,
                    }))}
                    styles={{
                        container: provided => ({
                            width: '290px',
                            ...provided,
                        }),
                    }}
                    onSelect={handleSelect}
                    menuPlacement="bottom"
                    isDisabled={videoDevices.length === 0 || error}
                />
            </DropDownWrapper>

            <ErrorMessageWrapper>
                {error && (
                    <>
                        <AppIcon
                            name="CameraOffIcon"
                            slashcolor={theme.colors.red50}
                            color={theme.colors.red50}
                        />
                        <AppText
                            semibold
                            size="medium"
                            color="red50"
                            style={{ display: 'block' }}
                            data-testid="video-settings-error-message"
                        >
                            {errorMessage}
                        </AppText>
                    </>
                )}
            </ErrorMessageWrapper>
        </Wrapper>
    );
};

const Wrapper = styled(Box)({
    marginBottom: '10px',
});

const VideoContainer = styled(Row)({
    justifyContent: 'left',
    alignItems: 'center',
    height: '270px',
});

const Video = styled.video(() => ({
    borderRadius: '20px',
    width: '445px',
    height: '270px',
}));

const PermissionDeniedContainer = styled(Column)(({ theme }) => ({
    justifyContent: 'center',
    alignItems: 'center',
    gap: '35px',
    borderRadius: '20px',
    width: '445px',
    height: '270px',
    backgroundColor: theme.colors.black,
}));

const DropDownWrapper = styled(Box)({
    marginTop: '16px',
});

const DropdownHeader = styled(Label)({
    display: 'block',
    margin: '5px 0px',
    fontSize: '1.1rem',
});

const ErrorMessageWrapper = styled(Row)({
    height: '24px',
    gap: '10px',
    marginTop: '1.2rem',
});
